import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { Globe, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import {
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷', badge: 'FR' },
  { code: 'en', label: 'English', flag: '🇬🇧', badge: 'EN' },
] as const;

export type SupportedLocale = (typeof LANGUAGES)[number]['code'];

interface LanguageSwitcherProps {
  /**
   * 'compact' (default): 1-click compact round toggle (same size as top bar buttons, size-8)
   * 'sub-menu': Sub-menu item for nested menus (like RailSidebar profile dropdown)
   */
  variant?: 'compact' | 'segmented' | 'popover' | 'sub-menu' | 'pill';
  className?: string;
  size?: 'sm' | 'md';
}

export function LanguageSwitcher({
  variant = 'compact',
  className,
  size = 'md',
}: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation('common');
  const { user, refetchUser } = useAuth();
  const currentLang = (i18n.language || 'fr').slice(0, 2).toLowerCase() as SupportedLocale;

  const handleLanguageChange = async (newLang: SupportedLocale) => {
    if (newLang === currentLang) return;

    if (typeof document !== 'undefined' && 'startViewTransition' in document) {
      (
        document as unknown as { startViewTransition: (cb: () => void) => void }
      ).startViewTransition(() => {
        void i18n.changeLanguage(newLang);
      });
    } else {
      await i18n.changeLanguage(newLang);
    }

    if (user) {
      try {
        await api.put('/auth/profile', {
          name: user.name,
          email: user.email,
          phone: user.phone,
          locale: newLang,
        });
        refetchUser();
      } catch {
        // Keep client-side locale update even if API request fails
      }
    }
  };

  const toggleLanguage = () => {
    const nextLang = currentLang === 'fr' ? 'en' : 'fr';
    void handleLanguageChange(nextLang);
  };

  // SUB-MENU VARIANT for RailSidebar / User menu
  if (variant === 'sub-menu') {
    const Sub =
      DropdownMenuSub ?? (({ children }: { children: React.ReactNode }) => <div>{children}</div>);
    const SubTrigger =
      DropdownMenuSubTrigger ??
      (({ children }: { children: React.ReactNode }) => <div>{children}</div>);
    const SubContent =
      DropdownMenuSubContent ??
      (({ children }: { children: React.ReactNode }) => <div>{children}</div>);
    const Portal =
      DropdownMenuPortal ??
      (({ children }: { children: React.ReactNode }) => <div>{children}</div>);

    return (
      <Sub>
        <SubTrigger className={cn('gap-2', className)}>
          <Globe className="size-4 text-slate-500" />
          <span className="flex-1 text-left">{t('language', { defaultValue: 'Langue' })}</span>
          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-600 dark:bg-charcoal-800 dark:text-slate-300">
            {currentLang.toUpperCase()}
          </span>
        </SubTrigger>
        <Portal>
          <SubContent className="w-36 rounded-xl border border-slate-200 bg-white p-1 shadow-md dark:border-charcoal-700 dark:bg-charcoal-900">
            {LANGUAGES.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className="flex cursor-pointer items-center justify-between rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-charcoal-800"
              >
                <span className="flex items-center gap-2">
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                </span>
                {currentLang === lang.code && (
                  <Check className="size-3.5 text-lime-600 dark:text-lime-400" />
                )}
              </DropdownMenuItem>
            ))}
          </SubContent>
        </Portal>
      </Sub>
    );
  }

  // DEFAULT COMPACT 1-CLICK TOGGLE — Same size as top toolbar buttons (size-8), clean flat design, animated roll
  return (
    <button
      type="button"
      role="radio"
      aria-checked={true}
      onClick={toggleLanguage}
      aria-label={t('language', { defaultValue: 'Changer de langue' })}
      title={`${t('language', { defaultValue: 'Changer de langue' })} (${currentLang === 'fr' ? 'English' : 'Français'})`}
      className={cn(
        'grid place-items-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-300 dark:hover:bg-charcoal-800 dark:hover:text-slate-50 overflow-hidden',
        size === 'sm' ? 'size-7 text-[10px]' : 'size-8 text-[11px]',
        className,
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={currentLang}
          initial={{ y: 6, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -6, opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="flex items-center gap-0.5 font-mono font-bold tracking-tight select-none"
        >
          <span className="text-[11px] leading-none">{currentLang === 'fr' ? '🇫🇷' : '🇬🇧'}</span>
          <span>{currentLang.toUpperCase()}</span>
        </motion.span>
      </AnimatePresence>
    </button>
  );
}
